import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AlertTriangle, RotateCcw, Trash2 } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/utils/formatters";
import { useTrash, useRestoreTrashItem, usePurgeTrashItem, type TrashItem } from "@/hooks/useAdminData";

export const Route = createFileRoute("/admin/trash/")({
  component: AdminTrashPage,
});

const TYPE_LABELS: Record<string, string> = {
  user: "Account",
  placement: "Placement",
};

function AdminTrashPage() {
  const [typeFilter, setTypeFilter] = useState("all");
  const [purgeTarget, setPurgeTarget] = useState<TrashItem | null>(null);

  const { data: items = [], isLoading } = useTrash();
  const restoreItem = useRestoreTrashItem();
  const purgeItem = usePurgeTrashItem();

  const filtered = useMemo(() => {
    if (typeFilter === "all") return items;
    if (typeFilter === "placement") return items.filter((i) => i.type === "placement");
    // Accounts are filtered by role so admins can separate students from partners.
    return items.filter((i) => i.type === "user" && i.role === typeFilter);
  }, [items, typeFilter]);

  const confirmPurge = () => {
    if (!purgeTarget) return;
    purgeItem.mutate(
      { type: purgeTarget.type, id: purgeTarget.id },
      { onSettled: () => setPurgeTarget(null) },
    );
  };

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Trash"
        subtitle="Deleted records are kept here. Restore them, or delete them permanently."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Trash" }]}
      />

      <SectionCard
        title={`Deleted records (${filtered.length})`}
        actions={
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[180px]" aria-label="Record type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="student">Students</SelectItem>
              <SelectItem value="partner">Partners</SelectItem>
              <SelectItem value="placement">Placements</SelectItem>
            </SelectContent>
          </Select>
        }
        noPadding
      >
        {isLoading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon={<Trash2 />}
            title="Trash is empty"
            description="Deleted students, partners and placements will appear here, where you can restore them or remove them for good."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Record</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const busy =
                  (restoreItem.isPending &&
                    restoreItem.variables?.id === item.id &&
                    restoreItem.variables?.type === item.type) ||
                  (purgeItem.isPending &&
                    purgeItem.variables?.id === item.id &&
                    purgeItem.variables?.type === item.type);

                return (
                  <TableRow key={`${item.type}-${item.id}`} className="h-11 [&>td]:py-1.5">
                    <TableCell className="min-w-[200px] pl-4 md:pl-5">
                      <p className="font-medium text-foreground">{item.label}</p>
                      {item.sublabel && (
                        <p className="text-caption text-muted-foreground">{item.sublabel}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {item.role ? item.role : TYPE_LABELS[item.type] || item.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDate(item.deleted_at)}
                    </TableCell>
                    <TableCell className="pr-4 md:pr-5">
                      <div className="flex justify-end gap-0.5">
                        <IconAction
                          label="Restore"
                          icon={<RotateCcw />}
                          disabled={busy}
                          onClick={() => restoreItem.mutate({ type: item.type, id: item.id })}
                        />
                        <IconAction
                          label="Delete permanently"
                          tone="destructive"
                          icon={<Trash2 />}
                          disabled={busy}
                          onClick={() => setPurgeTarget(item)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div
              className="mb-2 flex size-10 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive"
              aria-hidden
            >
              <AlertTriangle className="size-5" />
            </div>
            <DialogTitle>Delete permanently</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">{purgeTarget?.label}</span> will be
              erased for good. This cannot be undone and the record cannot be restored afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPurgeTarget(null)}
              disabled={purgeItem.isPending}
            >
              Keep in Trash
            </Button>
            <Button variant="destructive" onClick={confirmPurge} disabled={purgeItem.isPending}>
              <AlertTriangle />
              {purgeItem.isPending ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
